import { FC, ReactNode, useMemo, useState } from 'react';
import { format } from 'date-fns';
import Select from 'react-select';
import { TranslateFn, useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import { HoldingClass, Partner } from 'types';
import {
  TradingOfferOperation,
  TradingOfferOperationStatus,
} from 'types/TradingOfferOperation';

import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';

import { TradingOffer } from 'types/TradingOffer';

import {
  statusBadgeMap,
  menuStatusOptions,
} from '../constants/tradingOfferOperationStatus';
import getOperationReference from '../utils/getOperationReference';
import getOfferReference from '../utils/getOfferReference';

type OperationTypeFilter = '' | 'BUY' | 'SELL';

type EquityMarketAdminPanelOperationsTableProps = {
  operations: TradingOfferOperation[];
  offers?: TradingOffer[];
  onViewOperationDetail: (operationId: string) => void;
  onUpdateOperationStatus?: (
    operationId: string,
    status: string,
    comment?: string
  ) => void;
  embedded?: boolean;
  /** When 'partner', shows OperationType/Counterparty columns and partner-specific filters */
  variant?: 'admin' | 'partner';
  /** Required when variant='partner' - filters operations and drives OperationType/Counterparty display */
  partnerId?: string | null;
};

const getPartyName = (party: string | Partial<Partner>) => {
  if (typeof party === 'string') return '-';
  return party?.name || '-';
};

const getPartyImage = (party: string | Partial<Partner>) => {
  if (typeof party === 'string') return '';
  return party?.image || '';
};

const getPartyInitials = (party: string | Partial<Partner>) => {
  const name = getPartyName(party);
  if (name === '-') return '-';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const ALL_STATUSES = 'ALL';

const statusFilterOptionsRaw: { value: string; label: string }[] = [
  { value: ALL_STATUSES, label: 'All' },
  { value: TradingOfferOperationStatus.PENDING, label: 'Pending' },
  { value: TradingOfferOperationStatus.KYC_REVIEW, label: 'KYCReview' },
  { value: TradingOfferOperationStatus.VALIDATION, label: 'PendingValidation' },
  {
    value: TradingOfferOperationStatus.PAYMENT_PENDING,
    label: 'PaymentPending',
  },
  {
    value: TradingOfferOperationStatus.TRANSFER_PENDING,
    label: 'TransferPending',
  },
  { value: TradingOfferOperationStatus.COMPLETED, label: 'Completed' },
  { value: TradingOfferOperationStatus.CANCELLED, label: 'Cancelled' },
  { value: TradingOfferOperationStatus.REJECTED, label: 'Rejected' },
];

const operationTypeFilterOptionsRaw: {
  value: OperationTypeFilter;
  label: string;
}[] = [
  { value: '', label: 'All' },
  { value: 'BUY', label: 'Purchase' },
  { value: 'SELL', label: 'Sale' },
];

const getPartyId = (party: string | Partial<Partner>): string | null => {
  if (!party) return null;
  if (typeof party === 'string') return party;
  return (party as Partner)?._id ?? null;
};

const getOperationType = (
  operation: TradingOfferOperation,
  currentPartnerId: string
): 'BUY' | 'SELL' => {
  const buyerId = getPartyId(operation.buyer);
  return buyerId === currentPartnerId ? 'BUY' : 'SELL';
};

const getCounterparty = (
  operation: TradingOfferOperation,
  currentPartnerId: string,
  t: TranslateFn
): string => {
  const isBuyer = getOperationType(operation, currentPartnerId) === 'BUY';
  const counterparty = isBuyer ? operation.seller : operation.buyer;
  const role = isBuyer ? t('Seller') : t('Buyer');
  return `${role}: ${getPartyName(counterparty)}`;
};

const isPartnerOperation = (
  operation: TradingOfferOperation,
  currentPartnerId: string
): boolean => {
  const buyerId = getPartyId(operation.buyer);
  const sellerId = getPartyId(operation.seller);
  return buyerId === currentPartnerId || sellerId === currentPartnerId;
};

const ALL_TYPE_FILTER = '' as OperationTypeFilter;

const EquityMarketAdminPanelOperationsTable: FC<
  EquityMarketAdminPanelOperationsTableProps
> = ({
  operations,
  offers = [],
  onViewOperationDetail,
  onUpdateOperationStatus,
  embedded = false,
  variant = 'admin',
  partnerId = null,
}) => {
  const { t } = useTranslate();
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [offerFilter, setOfferFilter] = useState(ALL_STATUSES);
  const [typeFilter, setTypeFilter] =
    useState<OperationTypeFilter>(ALL_TYPE_FILTER);

  const isPartnerView = variant === 'partner';

  const partnerOperations = useMemo(() => {
    if (!isPartnerView || !partnerId) return operations;
    return operations.filter((op) => isPartnerOperation(op, partnerId));
  }, [operations, isPartnerView, partnerId]);

  const offerFilterOptions = useMemo(() => {
    if (isPartnerView) return [];
    const offerIds = new Set(partnerOperations.map((op) => op.tradingOffer));
    const relevantOffers = offers.filter((o) => offerIds.has(o._id));

    return [
      { value: ALL_STATUSES, label: t('AllOffers') },
      ...relevantOffers.map((o) => ({
        value: o._id,
        label: getOfferReference(o),
      })),
    ];
  }, [offers, partnerOperations, t, isPartnerView]);

  const statusFilterOptions = useMemo(
    () =>
      statusFilterOptionsRaw.map((opt) => ({
        value: opt.value,
        label: t(opt.label),
      })),
    [t]
  );

  const operationTypeFilterOptions = useMemo(
    () =>
      operationTypeFilterOptionsRaw.map((opt) => ({
        value: opt.value,
        label: t(opt.label),
      })),
    [t]
  );

  const filteredOperations = useMemo(() => {
    let result = partnerOperations;
    if (!isPartnerView && offerFilter !== ALL_STATUSES) {
      result = result.filter((op) => op.tradingOffer === offerFilter);
    }
    if (statusFilter !== ALL_STATUSES) {
      result = result.filter((op) => op.status === statusFilter);
    }
    if (isPartnerView && typeFilter && partnerId) {
      result = result.filter(
        (op) => getOperationType(op, partnerId) === typeFilter
      );
    }
    return result;
  }, [
    partnerOperations,
    statusFilter,
    offerFilter,
    typeFilter,
    isPartnerView,
    partnerId,
  ]);

  const isTerminalStatus = (status: TradingOfferOperationStatus) =>
    status === TradingOfferOperationStatus.COMPLETED ||
    status === TradingOfferOperationStatus.CANCELLED ||
    status === TradingOfferOperationStatus.REJECTED;

  const isPendingForPartner = (
    op: TradingOfferOperation,
    currentPartnerId: string
  ): boolean => {
    if (getOperationType(op, currentPartnerId) !== 'BUY') return false;

    const pendingStatuses: TradingOfferOperationStatus[] = [
      TradingOfferOperationStatus.KYC_REVIEW,
      TradingOfferOperationStatus.PAYMENT_PENDING,
    ];
    return pendingStatuses.includes(op.status);
  };

  const getTypeBadge = (type: 'BUY' | 'SELL') => {
    if (type === 'BUY') {
      return (
        <div className="d-flex align-items-center">
          <div className="icon-circle bg-primary-dim text-primary mr-2">
            <em className="icon ni ni-arrow-down-circle" />
          </div>
          <span className="badge badge-dim badge-primary">{t('Purchase')}</span>
        </div>
      );
    }
    return (
      <div className="d-flex align-items-center">
        <div className="icon-circle bg-success-dim text-success mr-2">
          <em className="icon ni ni-arrow-up-circle" />
        </div>
        <span className="badge badge-dim bg-success">{t('Sale')}</span>
      </div>
    );
  };

  const renderActionsCell = (op: TradingOfferOperation) => {
    if (isPartnerView) {
      return (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => onViewOperationDetail(op._id)}
        >
          {t('ViewDetail')}
        </button>
      );
    }
    if (!isTerminalStatus(op.status) && onUpdateOperationStatus) {
      return (
        // @ts-ignore
        <OperationMenuDots
          title={t('ManageOperation')}
          variant="primary"
          params={{ outlined: true }}
          menuOptions={[
            ...menuStatusOptions.map((status) => ({
              icon: status.icon,
              text: t(status.key),
              action: () => onUpdateOperationStatus(op._id, status.key),
              dropdown: true,
            })),
            {
              icon: 'ni-cross-circle',
              text: t('Rejected'),
              action: () =>
                onUpdateOperationStatus(
                  op._id,
                  TradingOfferOperationStatus.REJECTED
                ),
              dropdown: true,
              borderTop: true,
            },
            {
              icon: 'ni-na',
              text: t('Cancelled'),
              action: () =>
                onUpdateOperationStatus(
                  op._id,
                  TradingOfferOperationStatus.CANCELLED
                ),
              dropdown: true,
            },
          ]}
        />
      );
    }
    return (
      <button
        type="button"
        className="btn btn-icon btn-trigger btn-sm"
        title={t('ViewDetail')}
        onClick={() => onViewOperationDetail(op._id)}
      >
        <em className="icon ni ni-arrow-right" />
      </button>
    );
  };

  const content: ReactNode = (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 px-4">
        <div className="d-flex align-items-center">
          {isPartnerView && (
            <div className="d-flex align-items-center mr-4">
              <label className="form-label mb-0 mr-1 text-soft small">
                {t('OperationType')}:
              </label>
              <Select
                className="react-select"
                styles={{ control: (base) => ({ ...base, minWidth: 140 }) }}
                value={operationTypeFilterOptions.find(
                  (o) => o.value === typeFilter
                )}
                options={operationTypeFilterOptions}
                onChange={(opt) =>
                  setTypeFilter(
                    (opt?.value ?? ALL_TYPE_FILTER) as OperationTypeFilter
                  )
                }
              />
            </div>
          )}

          {!isPartnerView && (
            <div className="d-flex align-items-center mr-4">
              <label className="form-label mb-0 mr-1 text-soft small">
                {t('Offers')}:
              </label>
              <Select
                className="react-select"
                styles={{ control: (base) => ({ ...base, minWidth: 160 }) }}
                value={offerFilterOptions.find((o) => o.value === offerFilter)}
                options={offerFilterOptions}
                onChange={(opt) => setOfferFilter(opt?.value ?? ALL_STATUSES)}
              />
            </div>
          )}

          <div className="d-flex align-items-center mr-4">
            <label className="form-label mb-0 mr-1 text-soft small">
              {t('Status')}:
            </label>
            <Select
              className="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minWidth: isPartnerView ? 180 : 160,
                }),
              }}
              value={statusFilterOptions.find((o) => o.value === statusFilter)}
              options={statusFilterOptions}
              onChange={(opt) => setStatusFilter(opt?.value ?? ALL_STATUSES)}
            />
          </div>
        </div>
      </div>

      {filteredOperations.length === 0 ? (
        <div className="text-center py-4">
          <em className="icon ni ni-inbox fs-3 text-soft d-block mb-2" />
          <p className="text-soft">{t('NoOperationsAvailable')}</p>
        </div>
      ) : (
        <div
          className={
            isPartnerView
              ? 'table-responsive'
              : 'table-responsive card card-bordered'
          }
        >
          <table className="table table-tranx table-hover custom-table mb-0">
            <thead>
              <tr className="tb-tnx-head">
                {isPartnerView && (
                  <>
                    <th className="tb-tnx-id w-auto">
                      <span>{t('Operation')}</span>
                    </th>
                    <th className="tb-tnx-id">
                      <span>{t('OfferRef')}</span>
                    </th>
                  </>
                )}
                <th className="tb-tnx-id">{t('Ref')}</th>
                <th className="tb-tnx-id">{t('Date')}</th>
                {isPartnerView ? (
                  <th className="tb-tnx-info">
                    <span>{t('Counterparty')}</span>
                  </th>
                ) : (
                  <>
                    <th className="tb-tnx-id">{t('Buyer')}</th>
                    <th className="tb-tnx-id">{t('Seller')}</th>
                  </>
                )}
                <th
                  className={
                    isPartnerView ? 'tb-tnx-info' : 'tb-tnx-info text-right'
                  }
                >
                  {t('Shares')}
                </th>
                <th
                  className={
                    isPartnerView ? 'tb-tnx-info' : 'tb-tnx-info text-right'
                  }
                >
                  {t('Amount')}
                </th>
                <th className="tb-tnx-info">{t('Status')}</th>
                <th className="tb-tnx-id text-right">{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOperations.map((op) => {
                const badge =
                  statusBadgeMap[op.status] || statusBadgeMap.PENDING;
                const opType =
                  isPartnerView && partnerId
                    ? getOperationType(op, partnerId)
                    : 'BUY';
                const pendingForPartner =
                  isPartnerView &&
                  !!partnerId &&
                  isPendingForPartner(op, partnerId);

                return (
                  <tr
                    key={op._id}
                    onClick={() => onViewOperationDetail(op._id)}
                    className={`tb-tnx-item cursor-pointer${
                      pendingForPartner ? ' bg-warning-dim' : ''
                    }`}
                  >
                    {isPartnerView && (
                      <>
                        {/* Offer Type cell */}
                        <td className="tb-tnx-id align-middle w-auto">
                          {getTypeBadge(opType)}
                        </td>

                        {/* Offer Reference cell */}
                        <td className="tb-tnx-id align-middle">
                          <span className="ref text-base badge badge-light">
                            {getOfferReference(op.tradingOffer as TradingOffer)}
                          </span>
                        </td>
                      </>
                    )}

                    {/* OperationReference cell */}
                    <td className="tb-tnx-id align-middle">
                      <span className="ref text-base badge badge-light">
                        {getOperationReference(op)}
                      </span>
                    </td>

                    {/* Date cell */}
                    <td className="tb-tnx-id align-middle">
                      {format(new Date(op.requestDate), dateFormat)}
                    </td>

                    {isPartnerView ? (
                      <>
                        {/* Counterparty cell */}
                        <td className="tb-tnx-info align-middle">
                          <span>
                            {partnerId
                              ? getCounterparty(op, partnerId, t)
                              : '-'}
                          </span>
                        </td>
                      </>
                    ) : (
                      <>
                        {/* Buyer cell */}
                        <td className="tb-tnx-id align-middle">
                          <span className="d-inline-flex align-items-center">
                            <div className="user-avatar user-avatar-xs bg-info-dim mr-1 w-25px h-25px fs-12px">
                              {getPartyImage(op.buyer) ? (
                                <img
                                  src={getPartyImage(op.buyer)}
                                  alt={getPartyName(op.buyer)}
                                />
                              ) : (
                                <span>{getPartyInitials(op.buyer)}</span>
                              )}
                            </div>
                            {getPartyName(op.buyer)}
                          </span>
                        </td>

                        {/* Seller cell */}
                        <td className="tb-tnx-id align-middle">
                          <span className="d-inline-flex align-items-center">
                            <div className="user-avatar user-avatar-xs bg-primary-dim mr-1 w-25px h-25px fs-12px">
                              {getPartyImage(op.seller) ? (
                                <img
                                  src={getPartyImage(op.seller)}
                                  alt={getPartyName(op.seller)}
                                />
                              ) : (
                                <span>{getPartyInitials(op.seller)}</span>
                              )}
                            </div>
                            {getPartyName(op.seller)}
                          </span>
                        </td>
                      </>
                    )}

                    {/* Shares cell */}
                    <td className="tb-tnx-info text-left align-middle">
                      {formatNumber(op.sharesCount)}
                    </td>

                    {/* Total amount cell */}
                    <td className="tb-tnx-info text-right align-middle">
                      <strong>{formatCurrencyDecimals(op.totalPrice)}</strong>
                    </td>

                    {/* Status cell */}
                    <td className="tb-tnx-info align-middle">
                      <span className={`badge badge-dim ${badge.className}`}>
                        <em className={`icon ni ${badge.icon} mr-1`} />
                        {t(badge.label)}
                      </span>
                      {pendingForPartner && (
                        <span className="badge badge-warning ml-1 mt-1 d-inline-block">
                          {t('PendingForYou')}
                        </span>
                      )}
                    </td>

                    {/* Actions cell */}
                    <td
                      className="tb-tnx-id align-middle text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderActionsCell(op)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="card card-bordered">
      <div className="card-inner">{content}</div>
    </div>
  );
};

export default EquityMarketAdminPanelOperationsTable;
